import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
}                                                                    from '@angular/core';
import {
  ClearEvent,
  ErrorEvent,
  FileRestrictions,
  FileState,
  RemoveEvent,
  SelectEvent,
  SuccessEvent,
  UploadComponent,
  UploadEvent,
  UploadProgressEvent
}                                                                    from '@progress/kendo-angular-upload';
import {AbstractControl, FormControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {BehaviorSubject, interval, Subscription}                     from 'rxjs';
import {
  Base64FileData,
  FileDownloadProcessCallback,
  FileInfoData,
  FilePreviewModalDialogData,
  FileValidationCallback,
  FileWithId,
  PreviewFileData,
  UploadFileEvent
}                                                                    from '../../types/ap-file-upload.types';
import {MediaFileService}                                            from '../../../services/common/media-file.service';
import {FileType, ThumbnailParam}                                    from '../../../types/file.types';
import {FileService}                                                 from '../../../services/common/file.service';
import {
  ApFileUploadComponentService
}                                                                    from '../../services/ap-file-upload-component.service';
import {ItemChangedEvent}                                            from '@progress/kendo-angular-scrollview';
import {saveAs}                                                      from '@progress/kendo-file-saver';
import {FileUploadHtmlService}                                       from '../../services/ap-file-upload-html.service';
import {DomSanitizer, SafeUrl}                                       from '@angular/platform-browser';
import {APP_CONFIGURATION}                                           from '../../../ap-core/config';
import {
  ApUploadFakeStreamService
}                                                                    from '../../services/ap-file-upload-fake-stream.service';
import {ApFileUploadMessagesDirective}                               from '../../derectives/ap-file-upload.derectives';
import {nameOf}                                                      from '../../../ap-core/utils/ap-name-of';
import {distinctUntilChanged, map, skip}                             from 'rxjs/operators';
import {DialogComponent}                                             from '@progress/kendo-angular-dialog';

/**
 * Component for handling file uploads with preview, validation, and download capabilities
 * Integrates with Kendo Upload and provides extended functionality
 */
@Component({
  selector: 'ap-file-upload',
  templateUrl: 'ap-file-upload.component.html',
  styleUrls: ['ap-file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ApFileUploadComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(private fileService: FileService,
              private mediaFileService: MediaFileService,
              private fileUploadHtmlService: FileUploadHtmlService,
              private fileUploadComponentService: ApFileUploadComponentService,
              private fileUploadFakeStream: ApUploadFakeStreamService,
              private elementRef: ElementRef,
              private sanitizer: DomSanitizer) {
  }

  /**
   * Gets all files currently in the upload component's file list
   * @returns Array of FileInfoData objects or empty array if no files exist
   * @public
   */
  public get getAllFiles(): FileInfoData[] {
    return (this.uploadFileComponent?.fileList?.filesFlat as FileInfoData[]) ?? [];
  }

  @Input()
  public control: FormControl;
  @Input()
  public saveUrl = APP_CONFIGURATION.UploadSaveUrl;
  @Input()
  public removeUrl = APP_CONFIGURATION.UploadRemoveUrl;
  @Input()
  public restrictions: FileRestrictions = {allowedExtensions: ['.txt', '.csv']};
  @Input()
  public autoUpload = false;
  @Input()
  public multiple = false;
  @Input()
  public showFilePreview = false;
  @Input()
  public viewFileOnClick = false;
  @Input()
  public withCredentials = false;
  @Input()
  public validateWithForm = false;
  @Input()
  public allowOnlyUniqFiles = false;
  @Input()
  public allowDownload = false;
  @Input()
  public disabled = false;
  @Input()
  public style: any = undefined;
  @Input()
  public dropFilesHereText = 'kendo.upload.dropFilesHere';
  @Input()
  public selectText = 'kendo.upload.select';
  @Input()
  public uploadSelectedFilesText = 'UploadUpload';
  @Input()
  public clearSelectedFilesText = 'Global__Cancel';
  @Input()
  public fileDownloadProcessCallback: FileDownloadProcessCallback | undefined;
  @Input()
  public customFileValidation: FileValidationCallback | undefined;

  @Output()
  public fileUploadProgress = new EventEmitter<any>();
  @Output()
  public fileUploadSuccess = new EventEmitter<any>();
  @Output()
  public fileUpload = new EventEmitter<any>();
  @Output()
  public fileSelect = new EventEmitter<any>();
  @Output()
  public fileRemove = new EventEmitter<any>();
  @Output()
  public fileError = new EventEmitter<any>();

  @ViewChild(UploadComponent, {read: ElementRef})
  public uploadComponentElementRef: ElementRef;

  public filePreviewModalDialogData: FilePreviewModalDialogData;
  public previewFilesData: PreviewFileData[] = [];

  protected readonly FileType = FileType;

  @ViewChild(UploadComponent)
  private uploadFileComponent: UploadComponent;
  @ViewChild(ApFileUploadMessagesDirective)
  private uploadMessages: ApFileUploadMessagesDirective;
  @ViewChild('filePreviewDialog')
  public filePreviewDialog: DialogComponent;

  private _viewContainer: HTMLElement;
  private _readyToUploadStatusClassName = 'ready-to-upload-status';
  private _initialFiles: FileInfoData[] = [];
  private _thumbnailSettings: ThumbnailParam;
  private _subscriptions: Subscription[] = [];
  private _isModalDialogOpened = false;
  private _defaultFilePreviewModalHeight = 700;

  /**
   * Initializes component by setting up view container, thumbnail settings,
   * file preview modal data, and form validation if enabled.
   * Also starts monitoring HTTP subscription properties of files.
   * @public
   */
  public ngOnInit(): void {
    this._viewContainer = this.elementRef.nativeElement.closest('ap-view-container');
    this._thumbnailSettings = this._settingsForThumbnail();
    this.filePreviewModalDialogData = this._generateFilePreviewModalDialogData();
    if (this.validateWithForm) {
      this.control.addValidators(this._filesValidator());
    }
    this._subscriptions.push(
      interval(100).pipe(
        map(() => JSON.stringify(this.getAllFiles.map(f => ({id: f.uid, status: f.state})))),
        distinctUntilChanged((previous, current) => previous === current),
        skip(1) // Make skip 1-st invoke, because 'distinctUntilChanged' always return undefined on first run
      ).subscribe(() => {
        if (this.getAllFiles.length !== 0) {
          this.fileUploadComponentService.modifyHttpSubscriptionProperties(this.getAllFiles);
          this._setValuesToControl(this.getAllFiles);
        } else {
          this.clearAllFilesData();
        }
      })
    );
  }

  /**
   * Performs post-view initialization setup by storing initial files
   * and adjusting their HTML data if files exist
   * @public
   */
  public ngAfterViewInit(): void {
    this._initialFiles = this.getAllFiles?.map(file => ({...file} as FileInfoData)) ?? [];
    if (this._initialFiles.length > 0) {
      this._adjustFileHtmlData(this._initialFiles).then();
      this._setValuesToControl(this._initialFiles);
    }
  }

  /**
   * Cleans up component resources by clearing preview data,
   * upload streams, and unsubscribing from all subscriptions
   * @public
   */
  public ngOnDestroy(): void {
    this._clearParticularPreviewFilesData(this.getAllFiles);
    this.fileUploadFakeStream.clearAllUploadStreams();
    this._subscriptions.forEach(subscription => subscription?.unsubscribe());
  }

  /**
   * Handles a window resize events by adjusting file preview modal dimensions
   * @public
   */
  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this.showFilePreview) {
      this._resizeFilePreviewModal();
    }
  }

  /**
   * Handles document click events to manage file preview modal closure
   * @param event$ Mouse click event$
   * @public
   */
  @HostListener('document:click', ['$event'])
  public onClick(event$: MouseEvent): void {
    if (!this.showFilePreview || !event$?.target) {
      return;
    }
    this._closeFilePreviewModal(event$.target);
  }

  /**
   * Handles file selection events by processing selected files,
   * adding previews if enabled, and updating an internal files collection.
   * Emits fileSelect event$ for parent component handling.
   * @param event$ File selection event$ containing selected files information
   * @public
   */
  public onFileSelect(event$: SelectEvent): void {
    const fileDataInfos = event$.files as FileInfoData[];
    this._adjustInformationForFilesOnSelect(fileDataInfos);
    this._onSelectFilesValidation(fileDataInfos);
    this._adjustFileHtmlData(fileDataInfos).then();
    this.fileSelect.emit(event$);
  }

  /**
   * Handles file removal by updating internal files collection
   * and emitting removal event for parent component handling
   * @param event$ RemoveEvent containing information about removed files
   * @public
   */
  public onFileRemove(event$: RemoveEvent): void {
    this._clearParticularPreviewFilesData(event$.files as FileInfoData[]);
    this.fileRemove.emit(event$);
  }

  /**
   * Handles clearing of selected files while preserving initial files.
   * Prevents default clear behavior if initial files exist and filters
   * the file list to keep only initial files.
   * @param event$ ClearEvent containing clear operation information
   * @public
   */
  public onFilesClear(event$: ClearEvent): void {
    if (this._initialFiles.length > 0) {
      event$.preventDefault();
      const filesToKeep = this._initialFiles.map(x => x.uid);
      const updatedFiles = this.getAllFiles.filter(x => filesToKeep.includes(x.uid));
      this._clearParticularPreviewFilesData(updatedFiles.filter(x => !filesToKeep.includes(x.uid)));
      this.control.setValue(updatedFiles);
      this._adjustFileHtmlData(updatedFiles).then();
    } else {
      this._clearParticularPreviewFilesData(this.getAllFiles);
    }
  }

  /**
   * Handles clearing of all files and related data to it.
   * @public
   */
  public clearAllFilesData(): void {
    this.clearFilePreviewData(this.previewFilesData);
    this._initialFiles = [];
    this._setValuesToControl([]);
  }

  /**
   * Handles file upload initialization and emits upload event
   * for parent component processing
   * @param event$ UploadEvent containing upload information
   * @public
   */
  public onFileUpload(event$: UploadEvent): void {
    event$.files.forEach(file => {
      (file.rawFile as FileWithId).uid = file.uid;
      this.fileUploadFakeStream.createUploadStream(file.uid);
    });
    this.fileUploadHtmlService.removeAllReadyToUpload(this.uploadComponentElementRef,
      this._readyToUploadStatusClassName);
    this.fileUpload.emit({
      files: event$.files,
      fileProcess: {
        setUploadFinished: (fileId: string) => this.fileUploadFakeStream.completeUpload(fileId),
        setUploadProgress: (fileId: string, progress: number) => this.fileUploadFakeStream.updateProgress(fileId, progress),
        setUploadFailed: (fileId: string) => this.fileUploadFakeStream.failUpload(fileId)
      }
    } as UploadFileEvent);
  }

  /**
   * Tracks and emits file upload progress events to parent component
   * @param event$ Upload progress event containing completion percentage
   * @public
   */
  public onFileUploadProgress(event$: UploadProgressEvent): void {
    this.fileUploadProgress.emit(event$);
  }

  /**
   * Handles successful file uploads and emits success event to parent component
   * @param event$ SuccessEvent containing upload result information
   * @public
   */
  public onFileUploadSuccess(event$: SuccessEvent): void {
    this.fileUploadSuccess.emit(event$);
  }

  /**
   * Handles file upload errors and emits error events to parent component
   * @param event$ ErrorEvent containing error information
   * @public
   */
  public onFileUploadError(event$: ErrorEvent): void {
    this.fileError.emit(event$);
  }

  /**
   * Closes the file preview dialog and resets all preview-related properties
   * @public
   */
  public onFilePreviewDialogClose(): void {
    this._isModalDialogOpened = false;
    this.filePreviewModalDialogData.ShowDialog = false;
    this.filePreviewModalDialogData.MediaFileData = undefined;
    this.filePreviewModalDialogData.ActiveIndex = 0;
  }

  /**
   * Handles file preview item selection changes by updating modal data
   * and loading the selected file's content
   * @param event$ Item change event containing selected item information
   * @public
   */
  public selectedFilePreview(event$: ItemChangedEvent): void {
    this.filePreviewModalDialogData.MediaFileData = event$.item;
    this.filePreviewModalDialogData.ActiveIndex = event$.index;
    this._loadFileData(event$.item);
  }

  /**
   * Creates a safe URL for file content display
   * @param url Raw URL string to be sanitized
   * @returns SafeUrl object that can be used in DOM
   * @public
   */
  public getSafeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  //#region Private Helper Methods

  /**
   * Updates form control value with copied files while preventing view updates
   * @param files Array of files to set as control value
   * @private
   */
  private _setValuesToControl(files: FileInfoData[]): void {
    const copiedFiles = files?.map(file => ({...file} as FileInfoData)) ?? [];
    this.control.setValue(copiedFiles, {emitEvent: true, emitModelToViewChange: false});
    if (this.validateWithForm) {
      this.control.updateValueAndValidity();
    }
  }

  /**
   * Creates form validator function that checks if all files are properly uploaded
   * and valid according to validation rules
   * @returns ValidatorFn that validates file upload state and validation errors
   * @private
   */
  private _filesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = control.value as FileInfoData[];
      if (!files?.length) {
        return null;
      }
      const isSomeOfFilesNotUploaded = files.some(x => x.state !== FileState.Uploaded);
      const isSomeOfFileAreInvalid = files.some(x => x.validationErrors?.length > 0);
      return isSomeOfFilesNotUploaded || isSomeOfFileAreInvalid ? {required: true} : null;
    };
  }

  /**
   * Validates selected files for uniqueness and custom validation rules
   * @param filesInfoData Array of files to validate
   * @private
   */
  private _onSelectFilesValidation(filesInfoData: FileInfoData[]): void {
    const allSelectedFiles = this.getAllFiles;
    filesInfoData.forEach(fileInfo => {
      if (!fileInfo.validationErrors || fileInfo.validationErrors.length === 0) {
        if (this.allowOnlyUniqFiles && this.fileUploadComponentService.isSameFileIsExist(fileInfo, allSelectedFiles)) {
          fileInfo.validationErrors = [nameOf<ApFileUploadMessagesDirective>('fileIsNotUnique')];
          return;
        }
        if (!!this.customFileValidation) {
          const validationResult = this.customFileValidation(fileInfo);
          if (!!validationResult) {
            this.uploadMessages.registerMessage([validationResult]);
            fileInfo.validationErrors = [validationResult.ErrorKey];
          }
        }
      }
    });
  }

  /**
   * Updates file information with MIME type and file type data
   * @param files Array of files to update
   * @private
   */
  private _adjustInformationForFilesOnSelect(files: FileInfoData[] | undefined): void {
    files?.forEach(fileDataInfo => {
      if (!fileDataInfo.rawFile) {
        return;
      }
      fileDataInfo.MimeType = fileDataInfo.rawFile.type;
      fileDataInfo.FileType = this.fileService.getFileType(fileDataInfo.rawFile.type);
    });
  }

  /**
   * Generates and configures thumbnail settings for media files
   * @returns ThumbnailParam object with configured settings
   * @private
   */
  private async _adjustFileHtmlData(files: FileInfoData[] | undefined): Promise<void> {
    for (const fileInfoData of files) {
      try {
        let previewFileData = this.previewFilesData.find(x => x.FileInfoData.uid === fileInfoData.uid);
        if (!previewFileData) {
          previewFileData = {
            FileInfoData: fileInfoData,
            ThumbnailDataUrl: undefined,
            ContentDataUrl: undefined,
            IsError$: new BehaviorSubject<boolean>(false),
            IsFileLoaded$: new BehaviorSubject<boolean>(false),
          };
          this.previewFilesData.push(previewFileData);
        }
        if (this.showFilePreview) {
          await this.generatePreviewHtmlLogic(fileInfoData, previewFileData);
        }
        if (!fileInfoData.validationErrors || fileInfoData.validationErrors.length === 0) {
          if (!!fileInfoData.UploadDate) {
            setTimeout(() => this.fileUploadHtmlService.addUploadDateToFile(this.uploadComponentElementRef, fileInfoData));
          }
          if (!this.autoUpload && this.fileUploadComponentService.isFileHaveInitOrSelectState(fileInfoData)) {
            setTimeout(() => this.fileUploadHtmlService.addReadyToUploadInfo(this.uploadComponentElementRef,
              fileInfoData, this._readyToUploadStatusClassName));
          }
          if (this.allowDownload) {
            setTimeout(() => this.fileUploadHtmlService.addDownloadButton(this.uploadComponentElementRef,
              previewFileData, this._onFileDownload.bind(this)));
          }
        }
      } catch (e) {
        console.warn(e);
      }
    }
  }

  /**
   * Generates preview HTML elements for file uploads with media handling.
   * - Creates content URLs and thumbnails for media files
   * - Configures preview click handlers
   * - Manages file loading states
   * - Adds download buttons if enabled
   * @param fileInfoData File information with raw data and metadata
   * @param previewFileData Preview data for storing URLs and states
   * @returns Promise resolving when preview generation completes
   * @private
   */
  private async generatePreviewHtmlLogic(fileInfoData: FileInfoData, previewFileData: PreviewFileData): Promise<void> {
    const contentDataUrl = previewFileData.ContentDataUrl ?? this._createObjectUrl(fileInfoData.rawFile);
    previewFileData.ContentDataUrl = contentDataUrl;
    previewFileData.IsFileLoaded$.next(!!contentDataUrl);
    if (this.fileUploadComponentService.isMediaFile(fileInfoData.FileType)) {
      if (!fileInfoData.ThumbnailFile && !!fileInfoData.rawFile) {
        fileInfoData.ThumbnailFile = await this.mediaFileService.generateThumbnail(
          fileInfoData.rawFile, this._thumbnailSettings);
      }
      if (!!fileInfoData.ThumbnailFile) {
        previewFileData.ThumbnailDataUrl = this._createObjectUrl(fileInfoData.ThumbnailFile);
        setTimeout(() => this.fileUploadHtmlService.addMediaFileThumbnail(this.uploadComponentElementRef,
          previewFileData, this._openPreview.bind(this, previewFileData)));
        return;
      }
    }
    setTimeout(() => this.fileUploadHtmlService.addFileIconClick(this.uploadComponentElementRef,
      previewFileData, this._openPreview.bind(this, previewFileData)));
  }

  /**
   * Initializes file preview modal dialog data structure
   * @returns FilePreviewModalDialogData object with default settings
   * @private
   */
  private _openPreview(previewFileData: PreviewFileData): void {
    const mediaDataIndex = this.previewFilesData.findIndex(x => x.FileInfoData.uid === previewFileData.FileInfoData.uid);
    if (mediaDataIndex === -1) {
      return;
    }
    this.filePreviewModalDialogData.MediaFileData = previewFileData;
    this.filePreviewModalDialogData.ActiveIndex = mediaDataIndex;
    this.filePreviewModalDialogData.Height$.next(this._getFilePreviewModalHeight());
    this.filePreviewModalDialogData.ShowDialog = true;
    this._loadFileData(previewFileData);
    setTimeout(() => this._isModalDialogOpened = true, 10);
  }

  /**
   * Handles file download requests by either using cached content or initiating a new download.
   * If a file is already loaded, downloads immediately using cached data.
   * Otherwise, triggers loading process and downloads after content is available.
   * Updates download button state during the process.
   * @param previewFileData The file data object containing download information
   * @private
   */
  private _onFileDownload(previewFileData: PreviewFileData): void {
    if (previewFileData.IsFileLoaded$.getValue()) {
      saveAs(previewFileData.ContentDataUrl, previewFileData.FileInfoData.name);
    } else {
      this.fileUploadHtmlService.downloadButtonState(this.uploadComponentElementRef, previewFileData.FileInfoData.uid, true);
      this._loadFileData(previewFileData, (previewFileDataAfterDownload: PreviewFileData | undefined) => {
        if (!!previewFileDataAfterDownload) {
          saveAs(previewFileDataAfterDownload.ContentDataUrl, previewFileDataAfterDownload.FileInfoData.name);
        }
        this.fileUploadHtmlService.downloadButtonState(this.uploadComponentElementRef, previewFileData.FileInfoData.uid, false);
      });
    }
  }

  /**
   * Loads file data for preview, handling both cached and uncached scenarios.
   * Manages loading states and error handling during a file download process.
   * @param previewFileData The file data to be loaded for preview
   * @param afterDownloadCallBack Optional callback to be executed after download completed
   * @private
   */
  private _loadFileData(previewFileData: PreviewFileData,
                        afterDownloadCallBack?: (previewFileData: PreviewFileData | undefined) => void): void {
    if (!!previewFileData.ContentDataUrl) {
      previewFileData.IsError$.next(false);
      previewFileData.IsFileLoaded$.next(true);
    } else {
      if (!!this.fileDownloadProcessCallback) {
        try {
          previewFileData.IsError$.next(false);
          previewFileData.IsFileLoaded$.next(false);
          this.fileDownloadProcessCallback(previewFileData.FileInfoData.uid, (base64FileData: Base64FileData | undefined) => {
            if (!!base64FileData && !!base64FileData.Base64) {
              this.fileUploadComponentService.updateRawFileData(previewFileData.FileInfoData, base64FileData);
              previewFileData.ContentDataUrl = this._createObjectUrl(previewFileData.FileInfoData.rawFile);
              previewFileData.IsError$.next(false);
              previewFileData.IsFileLoaded$.next(true);
              if (!!afterDownloadCallBack) {
                afterDownloadCallBack(previewFileData);
              }
            } else {
              previewFileData.IsError$.next(false);
              previewFileData.IsFileLoaded$.next(true);
              if (!!afterDownloadCallBack) {
                afterDownloadCallBack(previewFileData);
              }
            }
          });
        } catch (error) {
          console.warn(error);
          previewFileData.IsError$.next(true);
          previewFileData.IsFileLoaded$.next(false);
          if (!!afterDownloadCallBack) {
            afterDownloadCallBack(previewFileData);
          }
        }
      } else {
        previewFileData.IsError$.next(true);
        previewFileData.IsFileLoaded$.next(false);
        if (!!afterDownloadCallBack) {
          afterDownloadCallBack(previewFileData);
        }
      }
    }
  }

  /**
   * Cleans up preview data for removed files by revoking object URLs
   * and removing them from the preview files collection
   * @param filesToRemove Array of files to be removed from preview
   * @private
   */
  private _clearParticularPreviewFilesData(filesToRemove: FileInfoData[]): void {
    const fileIdsToRemove = filesToRemove.map(x => x.uid);
    const filePreviewsToDelete = this.previewFilesData.filter(x => fileIdsToRemove.includes(x.FileInfoData.uid));
    this.clearFilePreviewData(filePreviewsToDelete);
    this.previewFilesData = this.previewFilesData.filter(x => !fileIdsToRemove.includes(x.FileInfoData.uid));
  }

  /**
   * Revokes object URLs for file previews to release memory
   * @param filePreviews Array of files to clear
   * @private
   */
  private clearFilePreviewData(filePreviews: PreviewFileData[]): void {
    filePreviews.forEach((filePreview: PreviewFileData) => {
      if (!!filePreview.ContentDataUrl) {
        URL.revokeObjectURL(filePreview.ContentDataUrl);
      }
      if (!!filePreview.ThumbnailDataUrl) {
        URL.revokeObjectURL(filePreview.ThumbnailDataUrl);
      }
    });
  }

  /**
   * Creates an object URL for a given file
   * @param file File object to create URL for
   * @returns Object URL string or undefined if no file provided
   * @private
   */
  private _createObjectUrl(file: File | undefined): string | undefined {
    return !!file ? URL.createObjectURL(file) : undefined;
  }

  /**
   * Updates the file preview modal height when modal is open
   * @private
   */
  private _resizeFilePreviewModal(): void {
    if (this._isModalDialogOpened) {
      this.filePreviewModalDialogData.Height$.next(this._getFilePreviewModalHeight());
    }
  }

  /**
   * Handles closing of file preview modal when clicking outside
   * @param eventTarget The DOM element that was clicked
   * @private
   */
  private _closeFilePreviewModal(eventTarget: EventTarget): void {
    if (this._isModalDialogOpened) {
      const element = document.querySelector('.k-window.k-dialog');
      if (!element) {
        return;
      }
      const dialogComponentElementRef = new ElementRef<any>(element);
      if (!!dialogComponentElementRef && !dialogComponentElementRef.nativeElement.contains(eventTarget)) {
        this.filePreviewDialog.close.emit();
      }
    }
  }

  /**
   * Calculates the appropriate height for the file preview modal
   * based on view container dimensions
   * @returns Calculated modal height in pixels
   * @private
   */
  private _getFilePreviewModalHeight(): number {
    if (!this._viewContainer) {
      return this._defaultFilePreviewModalHeight;
    }
    return this._viewContainer.offsetHeight - 200;
  }

  /**
   * Configures thumbnail generation parameters
   * @returns ThumbnailParam object with predefined settings
   * @private
   */
  private _settingsForThumbnail(): ThumbnailParam {
    return {
      Width: 200,
      Height: 200,
      Quality: 0.2,
      MimeType: 'image/webp',
      Name: 'thumbnail.webp'
    };
  }

  /**
   * Initializes the file preview modal dialog data structure
   * with default values
   * @returns FilePreviewModalDialogData object with initial settings
   * @private
   */
  private _generateFilePreviewModalDialogData(): FilePreviewModalDialogData {
    return {
      ShowDialog: false,
      MediaFileData: undefined,
      ActiveIndex: 0,
      Height$: new BehaviorSubject<number>(this._defaultFilePreviewModalHeight)
    };
  }

  //#endregion
}
